import { type InferenceModel, ModelModality, VectorType } from '@qdrant/qdrant-cloud-public-api/booking/v1';
import { convertToReadableUnitPrice } from '@/utils/booking-utils';

export const ORDERED_MODELS_GROUPS = ['dense', 'sparse', 'multi', 'image', 'external', 'other'] as const;
export type GroupType = (typeof ORDERED_MODELS_GROUPS)[number];

const KNOWN_VECTOR_TYPES = ['dense', 'sparse', 'multi', 'image'] as const;
const isKnownVectorType = (type: string): type is (typeof KNOWN_VECTOR_TYPES)[number] =>
  KNOWN_VECTOR_TYPES.includes(type as (typeof KNOWN_VECTOR_TYPES)[number]);

export const groupTitles: Record<GroupType, string> = {
  dense: 'Dense Text',
  external: 'External',
  image: 'Dense Image',
  multi: 'Multi Vector',
  other: 'Other',
  sparse: 'Sparse Text',
};

export const getVectorTypeString = (vectorType: VectorType) => {
  switch (vectorType) {
    case VectorType.DENSE:
      return 'dense';
    case VectorType.SPARSE:
      return 'sparse';
    case VectorType.MULTI:
      return 'multi';
    default:
      return 'other';
  }
};

export const getModelTypeString = (modality: ModelModality) => {
  switch (modality) {
    case ModelModality.TEXT:
      return 'text';
    case ModelModality.IMAGE:
      return 'image';
    default:
      return 'other';
  }
};

export const getGroupedModelsAndDefault = (models: InferenceModel[]) => {
  // Keys follow ORDERED_MODELS_GROUPS: `Object.entries` order is the order the groups render in.
  // oxlint-disable-next-line sort-keys
  const groupedModels: Record<GroupType, InferenceModel[]> = {
    dense: [],
    sparse: [],
    multi: [],
    image: [],
    external: [],
    // 'other' is used for any model that does not fit the 'known' vector types
    other: [],
  };

  for (const model of models) {
    const mappedType = model.modality === ModelModality.IMAGE ? 'image' : getVectorTypeString(model.vectorType);
    if (model.isExternal) {
      groupedModels.external.push(model);
    } else if (isKnownVectorType(mappedType)) {
      groupedModels[mappedType].push(model);
    } else {
      groupedModels.other.push(model);
    }
  }

  // sort models
  for (const group of Object.keys(groupedModels)) {
    groupedModels[group as GroupType].sort((a, b) => {
      // free models first
      if ((a.unitIntPrice || 0) === 0 && (b.unitIntPrice || 0) > 0) {
        return -1;
      }
      if ((a.unitIntPrice || 0) > 0 && (b.unitIntPrice || 0) === 0) {
        return 1;
      }
      // then by title
      return a.title.localeCompare(b.title);
    });
  }

  // From the ordered groups we find the first group that has models available
  // and select the first model from that group as the default model
  const firstAvailable = ORDERED_MODELS_GROUPS.find((type) => groupedModels[type]?.length);
  const defaultModel = firstAvailable ? groupedModels[firstAvailable][0] : undefined;

  return { groupedModels, defaultModel };
};

export const getCostStr = (model: InferenceModel) => {
  if (!model.unitIntPrice || model.unitIntPrice <= 0) {
    if (model.isExternal) {
      return 'At Provider Rate';
    }
    return 'Free';
  }
  return `${convertToReadableUnitPrice(model.unitIntPrice)} per 1M tokens`;
};
